/* =====================================================================
 * GuideArticle 스타일.
 *
 * 채팅 말풍선용 `MarkdownRenderer` 와 달리 코드블록 머리줄·복사 버튼 같은
 * 대화용 장식을 두지 않는다. 글자 크기는 앱 본문(14px)에 맞추고, 위계는
 * 크기보다 굵기·색으로 준다 — 전부 크게 잡으면 제목과 본문이 한 덩어리로
 * 읽힌다. 색·라운드·폰트는 모두 테마 토큰에서 읽는다.
 * ===================================================================== */

import styled, { css } from 'styled-components';

import type { GuideCalloutKind } from './rehypeGuideCallout';

const RAIL_BREAKPOINT = '1080px';

/* 스크롤 컨테이너 폭 기준으로 오른쪽 목차를 켜고 끈다. 뷰포트 기준이면 메인
 * 사이드바·가이드 목차가 펼쳐졌는지에 따라 본문이 좁아져도 모른다. */
export const ArticleLayout = styled.div`
  container-type: inline-size;
  width: 100%;
`;

export const ArticleGrid = styled.div<{ $tocOpen: boolean }>`
  display: flex;
  justify-content: center;
  gap: 56px;
  max-width: 1040px;
  margin: 0 auto;
  padding: 40px 40px 96px;
  padding-left: ${({ $tocOpen }) => ($tocOpen ? '40px' : '64px')};
  transition: padding-left ${({ theme }) => theme.transitions.fast};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 24px 16px 64px;
  }
`;

export const Article = styled.article`
  flex: 1;
  min-width: 0;
  max-width: 720px;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.fonts.sans};
  /* 한국어는 어절 단위로 줄을 바꾼다. 기본값이면 "창구입니다" 가 "창 / 구입니다"
   * 처럼 낱글자에서 잘린다. 긴 파일명·영문은 anywhere 로 넘치지 않게 한다. */
  word-break: keep-all;
  overflow-wrap: anywhere;
`;

/* ---------------- 머리말 ---------------- */

export const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const Title = styled.h1`
  margin: 10px 0 0;
  font-size: 1.625rem;
  line-height: 1.35;
  font-weight: 700;
  letter-spacing: -0.02em;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.375rem;
  }
`;

export const Meta = styled.p`
  margin: 8px 0 0;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const Intro = styled.div`
  margin-top: 18px;
  font-size: 0.9375rem;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.textSecondary};

  p {
    margin: 0 0 12px;
  }
`;

/* 문서 첫머리의 목차. 오른쪽 목차가 없는 좁은 화면에서도 전체 흐름을 먼저
 * 보여주기 위해 항상 그린다. */
export const Contents = styled.nav`
  margin: 28px 0 8px;
  padding: 20px 24px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  background: ${({ theme }) => theme.colors.bgSecondary};
`;

export const ContentsTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 10px;
`;

export const ContentsList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: guide-contents;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 24px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }

  li {
    counter-increment: guide-contents;
  }

  /* 앱 전역 a:hover 밑줄이 번호(::before)까지 번지므로 링크 자체의 밑줄은
   * 끄고, 글자 색만 바꾼다. 번호는 색도 그대로 둔다. */
  a {
    display: flex;
    gap: 10px;
    font-size: 0.875rem;
    line-height: 1.7;
    color: ${({ theme }) => theme.colors.textPrimary};
    text-decoration: none;

    &::before {
      content: counter(guide-contents);
      min-width: 1.25em;
      color: ${({ theme }) => theme.colors.textMuted};
      font-variant-numeric: tabular-nums;
    }

    &:hover,
    &:focus-visible {
      text-decoration: none;
      color: ${({ theme }) => theme.colors.brand};
    }
  }
`;

/* ---------------- 본문 ---------------- */

export const Body = styled.div`
  font-size: 0.875rem;
  line-height: 1.75;

  /* 절 사이에 가는 선을 둬서, 긴 문서를 훑을 때 어디서 주제가 바뀌는지 보이게 한다. */
  h2 {
    position: relative;
    margin: 48px 0 12px;
    padding-top: 40px;
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
    font-size: 1.1875rem;
    line-height: 1.4;
    font-weight: 700;
    letter-spacing: -0.01em;
    scroll-margin-top: 0;
  }

  /* 목차 박스 바로 아래 첫 절에는 선을 긋지 않는다 — 박스 테두리와 겹쳐 보인다. */
  & > h2:first-child {
    margin-top: 44px;
    padding-top: 0;
    border-top: none;
  }

  h3 {
    margin: 28px 0 6px;
    font-size: 0.9375rem;
    line-height: 1.5;
    font-weight: 600;
  }

  p {
    margin: 0 0 10px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  /* 문단 바로 뒤에 오는 목록은 그 문단의 설명이라 붙여 둔다. */
  p + ul,
  p + ol {
    margin-top: -2px;
  }

  /* 소제목 바로 아래 문단은 제목과 한 덩어리로 읽히게 가깝게. */
  h3 + p {
    margin-top: 0;
  }

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  ul,
  ol {
    margin: 0 0 16px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  ul {
    padding-left: 20px;
  }

  ul > li {
    margin: 4px 0;
    padding-left: 2px;
  }

  ul > li::marker {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  /* 번호 목록 — 따라 하는 단계는 차분한 회색 원, 캡처 속 번호를 풀이하는
   * 범례(data-legend)는 캡처의 번호 표시와 같은 파란 원으로 그린다. 둘을 같은
   * 색으로 두면 모든 단계가 "캡처 어딘가를 가리키는 번호" 처럼 읽힌다. */
  ol {
    list-style: none;
    padding-left: 0;
    counter-reset: guide-step;
  }

  ol > li {
    position: relative;
    margin: 6px 0;
    padding-left: 32px;
    counter-increment: guide-step;
  }

  ol > li::before {
    content: counter(guide-step);
    position: absolute;
    left: 0;
    top: 2px;
    width: 20px;
    height: 20px;
    box-sizing: border-box;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.bgPrimary};
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 18px;
    text-align: center;
  }

  ol[data-legend] > li::before {
    border-color: transparent;
    background: ${({ theme }) => theme.colors.brand};
    color: ${({ theme }) => theme.colors.brandContrastText};
    font-weight: 700;
  }

  a {
    color: ${({ theme }) => theme.colors.textLink};
    text-underline-offset: 3px;
  }

  hr {
    margin: 40px 0;
    border: none;
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  }

  table {
    width: 100%;
    margin: 8px 0 20px;
    border-collapse: separate;
    border-spacing: 0;
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
    overflow: hidden;
    font-size: 0.8125rem;
  }

  th,
  td {
    padding: 10px 14px;
    text-align: left;
    vertical-align: top;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  }

  tr:last-child td {
    border-bottom: none;
  }

  th {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textSecondary};
    background: ${({ theme }) => theme.colors.bgSecondary};
    white-space: nowrap;
  }

  td:first-child {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textPrimary};
    white-space: nowrap;
  }

  td {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

/* 인라인 코드는 이 문서에서 버튼 이름·단축키 표기로만 쓴다.
 * 줄 높이를 밀어 올리지 않도록 작고 납작하게 — 굵은 아래 테두리나 큰 여백을
 * 주면 이 표기가 든 줄만 간격이 벌어져 문단이 들쭉날쭉해 보인다. */
export const Key = styled.kbd`
  display: inline;
  padding: 1px 5px;
  margin: 0 1px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
`;

export const Figure = styled.figure`
  margin: 20px 0 24px;
`;

/* 캡처는 가장자리 여백을 잘라 두었으므로 따로 판을 깔지 않고 얇은 테두리만
 * 둘러 본문과 구분한다. 버튼인 이유는 눌러서 크게 보기 위해서다. */
export const FigureFrame = styled.button`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: zoom-in;

  img,
  video {
    display: block;
    /* 앱 전역 스타일이 img 를 폭 100% 로 늘리므로 명시적으로 되돌린다. */
    width: auto;
    max-width: 100%;
    height: auto;
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    border-radius: ${({ theme }) => theme.sizes.borderRadius};
    background: ${({ theme }) => theme.colors.bgPrimary};
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.sizes.borderRadius};
  }
`;

/* 캡처를 누르면 확대된다는 신호. 평소엔 옅게, 마우스를 올리면 또렷하게. */
export const ZoomHint = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textMuted};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  opacity: 0;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  ${FigureFrame}:hover &,
  ${FigureFrame}:focus-visible & {
    opacity: 1;
  }
`;

export const FigureCaption = styled.figcaption`
  margin-top: 8px;
  font-size: 0.75rem;
  line-height: 1.5;
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const calloutTone = {
  note: css`
    background: ${({ theme }) => theme.colors.bgSecondary};
    border-color: ${({ theme }) => theme.colors.borderLight};
  `,
  tip: css`
    background: ${({ theme }) => theme.colors.brandSoft};
    border-color: transparent;
  `,
  warning: css`
    background: ${({ theme }) => theme.colors.warningBg};
    border-color: transparent;
  `,
} satisfies Record<GuideCalloutKind, ReturnType<typeof css>>;

export const Callout = styled.aside<{ $kind: GuideCalloutKind }>`
  display: flex;
  gap: 10px;
  margin: 16px 0 20px;
  padding: 14px 16px;
  border: 1px solid;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  ${({ $kind }) => calloutTone[$kind]}

  p {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.7;
  }

  p + p {
    margin-top: 6px;
  }
`;

const calloutAccent = (kind: GuideCalloutKind) =>
  css`
    color: ${({ theme }) =>
      kind === 'warning' ? theme.colors.warning : kind === 'tip' ? theme.colors.brand : theme.colors.textMuted};
  `;

export const CalloutIcon = styled.span<{ $kind: GuideCalloutKind }>`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 3px;
  ${({ $kind }) => calloutAccent($kind)}
`;

export const CalloutBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const CalloutLabel = styled.div<{ $kind: GuideCalloutKind }>`
  margin-bottom: 2px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ $kind, theme }) =>
    $kind === 'warning' ? theme.colors.warning : $kind === 'tip' ? theme.colors.brand : theme.colors.textPrimary};
`;

/* ---------------- 오른쪽 목차 ---------------- */

export const Rail = styled.nav`
  display: none;
  width: 200px;
  flex-shrink: 0;

  @container (min-width: ${RAIL_BREAKPOINT}) {
    display: block;
  }
`;

export const RailSticky = styled.div`
  position: sticky;
  top: 40px;
`;

export const RailTitle = styled.div`
  margin-bottom: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const RailList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  border-left: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

export const RailLink = styled.a<{ $active: boolean }>`
  display: block;
  margin-left: -1px;
  padding: 5px 0 5px 14px;
  border-left: 2px solid ${({ $active, theme }) => ($active ? theme.colors.brand : 'transparent')};
  font-size: 0.78125rem;
  line-height: 1.5;
  text-decoration: none;
  color: ${({ $active, theme }) => ($active ? theme.colors.textPrimary : theme.colors.textMuted)};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};

  &:hover {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* ---------------- 맨 위로 ---------------- */

/* 본문 오른쪽 아래에 떠 있는 버튼. 스크롤하는 동안에만 나타나고 멈추면 흐려져 사라진다.
 * ArticleLayout 이 container 라 fixed 는 뷰포트가 아니라 본문 기준이 되므로, 본문 끝에
 * 높이 0 으로 붙인 sticky 슬롯에 둔다 — 스크롤 컨테이너 바닥에 달라붙는다. */
export const FloatingTopSlot = styled.div`
  position: sticky;
  bottom: 28px;
  display: flex;
  justify-content: flex-end;
  height: 0;
  padding-right: 28px;
  pointer-events: none;
`;

export const FloatingTopButton = styled.button<{ $visible: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  transform: translateY(${({ $visible }) => ($visible ? '-100%' : 'calc(-100% + 6px)')});
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.bgPrimary};
  box-shadow: ${({ theme }) => theme.shadows.md};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition:
    opacity ${({ theme }) => theme.transitions.normal},
    transform ${({ theme }) => theme.transitions.normal},
    color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* ---------------- 이전 / 다음 문서 ---------------- */

export const Siblings = styled.nav`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 56px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

export const SiblingLink = styled.button<{ $align?: 'start' | 'end' }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $align }) => ($align === 'end' ? 'flex-end' : 'flex-start')};
  gap: 4px;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  cursor: pointer;
  text-align: ${({ $align }) => ($align === 'end' ? 'right' : 'left')};
  transition: border-color ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.brand};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

export const SiblingHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const SiblingLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const SiblingBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* ---------------- 절 링크 복사 ---------------- */

/* 절 제목 왼쪽 여백에 떠 있다가, 제목에 마우스를 올리면 나타난다. */
export const AnchorButton = styled.button`
  position: absolute;
  left: -34px;
  bottom: 2px;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  opacity: 0;
  transition:
    opacity ${({ theme }) => theme.transitions.fast},
    background ${({ theme }) => theme.transitions.fast};

  h2:hover > &,
  &:focus-visible,
  &[data-copied] {
    opacity: 1;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &[data-copied] {
    color: ${({ theme }) => theme.colors.success};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 1px;
  }

  /* 좁은 화면에는 왼쪽 여백이 없어 제목 뒤에 붙인다. */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    position: static;
    margin-right: 4px;
    vertical-align: middle;
    opacity: 1;
  }
`;

export const AnchorTip = styled.span`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  padding: 3px 8px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.bgTooltip};
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 500;
  letter-spacing: 0;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  ${AnchorButton}:hover > &,
  ${AnchorButton}[data-copied] > & {
    opacity: 1;
  }
`;

/* ---------------- 자주 묻는 질문 ---------------- */

export const Faq = styled.details`
  margin: 10px 0;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  background: ${({ theme }) => theme.colors.bgPrimary};
  transition: border-color ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.border};
  }

  & > div {
    padding: 0 20px 16px 46px;
  }

  & > div > p:last-child,
  & > div > ul:last-child {
    margin-bottom: 0;
  }
`;

export const FaqQuestion = styled.summary`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  cursor: pointer;
  list-style: none;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textPrimary};

  &::-webkit-details-marker {
    display: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: -2px;
    border-radius: ${({ theme }) => theme.sizes.borderRadius};
  }
`;

export const FaqChevron = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  transform: rotate(-90deg);
  transition: transform ${({ theme }) => theme.transitions.fast};

  ${Faq}[open] & {
    transform: rotate(0deg);
  }
`;
